import React, { useState, useRef, useEffect } from 'react';
import { formatTextWithLinks } from '../../utils/formatTextWithLinks';
import { pallette } from '../../config/pallette';
import { io } from 'socket.io-client';

import SendAltIcon from '../../components/Icons/SendAltIcon';
import AlertIcon from '../../components/Icons/AlertIcon';
import ChatbotMenu from '../../components/ChatbotMenu';
import Feedback from '../../components/Feedback';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import chatbotLogo from '../../assets/chatbot_logo.svg';

import {
  Container,
  MessageUser,
  MessageBot,
  MessageBotLoading,
  MessageError,
  MessageContainer,
  AllMessagesContainer,
  MessageContainerUser,
  MessageContainerBot,
  Input,
  ChatContainer,
  InputContainer,
  RetryButton,
  SendButton,
  MessageBotImage,
  ChatbotTitle,
  ClearChatButton,
  InputWrapper,
  ClearChatButtonText,
  AllMessagesContent,
  Spinner,
  SpinnerBotReplying,
  InputTextLengthIndicator,
  SendContainer,
  CaveatTextContainer,
  CaveatText,
  CaveatTextLink
} from '../../pages/Chatbot/styles';
import ChatbotMenuMobile from '../../components/ChatbotMenuMobile';

const dateNow = Date.now();

const Chatbot = () => {
  const [chatbotSessionId, setChatbotSessionId] = useState(null);
  const [sessionId, setSessionId] = useState(dateNow);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [inputText, setInputText] = useState('');
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [isBotReplying, setIsBotReplying] = useState(false);
  const [isError, setIsError] = useState(false);
  const [sessionExpiredError, setSessionExpiredError] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const { token, logout, userId } = useAuth();
  const maxInputLength = 500;

  const allMessagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (allMessagesRef.current) {
      allMessagesRef.current.scrollTop = allMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!isBotReplying) {
      focusInput();
    }
  }, [isBotReplying]);

  useEffect(() => {
    // Restart session after 8 hours
    const timeout = setTimeout(() => {
      window.location.reload();
    }, 8 * 60 * 60 * 1000); // 8 hours in milliseconds

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (chatbotSessionId) {
      const socketConnection = io(process.env.REACT_APP_API_URL);
      socketConnection.emit('start-session', chatbotSessionId);

      // Cleanup when the user leaves or closes the tab
      const handleUnload = () => {
        // You can also emit some custom event here if needed, like 'session-end'
        // but disconnect will happen automatically when the socket is closed
      };

      window.addEventListener('beforeunload', handleUnload);
      return () => {
        window.removeEventListener('beforeunload', handleUnload);
        socketConnection.close(); // Socket automatically handles 'disconnect'
      };
    }
  }, [chatbotSessionId]);

  const handleInputChange = (event) => {
    setInputText(event.target.value);
  };

  const handleScrollDown = () => {
    if (allMessagesRef.current) {
      allMessagesRef.current.scrollBy({ top: 200, behavior: "smooth" });
    }
  };

  const streamResponse = async (userMessage, chatbotSession) => {
    const controller = new AbortController(); // For canceling if needed
    fetch(`${process.env.REACT_APP_API_URL}/api/ask`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        message: userMessage.text,
        sessionId,
        token,
        conversationId: currentConversation,
        userId,
        chatbotSessionId: chatbotSession
      }),
      signal: controller.signal
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Network response was not ok");

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let partial = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          partial += decoder.decode(value, { stream: true });

          // Optional: Process line-separated JSON chunks
          const lines = partial.split("\n").filter(Boolean);

          for (let line of lines) {
            try {
              const parsed = JSON.parse(line);

              if (parsed.type === "chunk") {
                if (!isBotThinking) setIsBotThinking(false);
                setMessages((prev) => {
                  const lastMessage = prev[prev.length - 1];
                  if (lastMessage?.sender === 'bot') {
                    // Update existing bot message
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                      ...lastMessage,
                      text: lastMessage.text + parsed.delta
                    };
                    return updated;
                  } else {
                    // First bot chunk — create new message
                    return [...prev, { text: parsed.delta, sender: 'bot' }];
                  }
                });
              }

              if (parsed.type === "done") {
                if (!conversations.find((item) => item.conversationId === parsed.conversationId)) {
                  setCurrentConversation(parsed.conversationId);
                  setConversations((prevConversations) => [
                    ...prevConversations,
                    {
                      conversationId: parsed.conversationId,
                      sessionId: sessionId,
                      title: inputText
                    },
                  ]);
                }

                if (parsed.fallback) {
                  setMessages((prev) => [...prev, { text: parsed.response, sender: 'bot' }]);
                }
                setIsBotThinking(false);
              }
            } catch (err) {
              // Probably partial JSON, wait for more data
              setIsBotThinking(false);
            }
          }
          // Reset buffer after processing
          partial = "";
        }
      })
      .catch((error) => {
        setIsBotThinking(false);
        setIsBotReplying(false);
        if (error.name === 'AbortError') {
          console.warn('Request was aborted');
        } else if (error.status === 401) {
          setSessionExpiredError(true);
        } else {
          setIsError(true);
          console.error('Streaming error:', error);
        }
      })
      .finally(() => {
        setIsBotReplying(false);
      });
  }

  const handleSendMessage = async () => {
    if (inputText.trim() === '') return;
    setIsError(false);

    const userMessage = { text: inputText, sender: 'user' };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputText('');

    setIsBotReplying(true);
    setIsBotThinking(true);

    let chatbotSession = chatbotSessionId;
    if (!chatbotSessionId) {
      try {
        const response = await api.post(`${process.env.REACT_APP_API_URL}/api/session`, {
          user_id: userId
        });
  
        if (response.status === 201) {
          chatbotSession = response.data.session_id;
          setChatbotSessionId(response.data.session_id);
        }
      } catch (error) {
        setIsBotReplying(false);
        setIsBotThinking(false);
        if (error.status === 401) {
          setSessionExpiredError(true);
        } else {
          setIsError(true);
          console.error('Error:', error);
        }
      }
    }

    if (chatbotSession) {
      await streamResponse(userMessage, chatbotSession);
    }
  };

  const retrySendMessage = async () => {
    setIsError(false);
    setIsBotReplying(true);
    const messageToRetry = messages[messages.length - 1];

    let chatbotSession = chatbotSessionId;
    if (!chatbotSessionId) {
      try {
        const response = await api.post(`${process.env.REACT_APP_API_URL}/api/session`, {
          user_id: userId
        });
  
        if (response.status === 201) {
          chatbotSession = response.data.session_id;
          setChatbotSessionId(response.data.session_id);
        }
      } catch (error) {
        setIsBotReplying(false);
        if (error.status === 401) {
          setSessionExpiredError(true);
        } else {
          setIsError(true);
          console.error('Error:', error);
        }
        return;
      }
    }

    if (chatbotSession) {
      await streamResponse(messageToRetry.text, chatbotSession);
    }
  };

  const handleClearChat = () => {
    const newDateNow = Date.now();

    // Filter out the current session from conversations
    const updatedConversations = conversations.filter(
      (conv) => conv.sessionId !== sessionId
    );

    setSessionId(newDateNow);
    setMessages([]);
    setCurrentConversation(null);
    setConversations(updatedConversations);
  };

  const handleExpiredSession = () => {
    const newDateNow = Date.now();
    logout();
    setSessionExpiredError(false);
    setSessionId(newDateNow);
    setMessages([]);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent the default behavior of Enter key
      handleSendMessage(); // Call handleSendMessage function on Enter key press
    }
  };

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const startNewChat = () => {
    const newDateNow = Date.now();
    setSessionId(newDateNow);
    setMessages([]);
    setCurrentConversation(null);
    setIsMenuOpen(false);
  };

  const fetchPreviousChat = async (conversationId, sessionId) => {
    setIsChatLoading(true);
    const response = await api.get(
      `${process.env.REACT_APP_API_URL}/api/conversation/${conversationId}`,
      {}
    );
    if (response.status === 200) {
      setCurrentConversation(conversationId);
      setSessionId(sessionId);
      const formattedMessages = response.data.messages
        .slice()
        .sort((a, b) => {
          const timeDiff = new Date(a.timestamp) - new Date(b.timestamp);
          if (timeDiff !== 0) return timeDiff; // Sort by timestamp (earliest first)
          return a.sender === 'user' ? -1 : 1; // If same timestamp, user comes first
        })
        .map((msg) => ({
          text: msg.message,
          sender: msg.sender,
        }));
      setMessages(formattedMessages);
    }
    setIsChatLoading(false);
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Layout
      noLoadingIndicator
      isChatbotPage={true}
      isMenuOpen={isMenuOpen}
      toggleMenu={() => setIsMenuOpen(!isMenuOpen)}
    >
      <Container>
        <ChatbotMenu
          handleLogout={logout}
          conversations={conversations}
          currentConversation={currentConversation}
          startNewChat={startNewChat}
          fetchPreviousChat={fetchPreviousChat}
          isBotReplying={isBotReplying}
        />
        {isMenuOpen && 
          <ChatbotMenuMobile
            handleLogout={logout}
            conversations={conversations}
            currentConversation={currentConversation}
            startNewChat={startNewChat}
            fetchPreviousChat={fetchPreviousChat}
            isBotReplying={isBotReplying}
          />}
        <ChatContainer>
          {messages.length > 0 ? (
            <AllMessagesContainer ref={allMessagesRef}>
              {isChatLoading ? (
                <Spinner />
              ) : (
                <AllMessagesContent>
                  {messages.map((message, index) => {
                    const isLastMessage = index === messages.length - 1;
                    const isBot = message.sender !== 'user';
                    const showFeedback = !(isLastMessage && isBot && isBotReplying);
                    
                    return (
                      <MessageContainer key={index}>
                        {message.sender === 'user' ? (
                          <MessageContainerUser>
                            <MessageUser>{message.text}</MessageUser>
                          </MessageContainerUser>
                        ) : (
                          <MessageContainerBot>
                            <MessageBotImage
                              src={chatbotLogo}
                              alt='Chatbot Logo'
                            />
                              <MessageBot
                                dynamic
                                dangerouslySetInnerHTML={formatTextWithLinks(
                                  message.text
                                )}
                              />
                            {showFeedback ? (
                              <Feedback
                                botResponse={message}
                                userPrompt={messages[index - 1]}
                                handleScrollDown={handleScrollDown}
                                conversationId={currentConversation}
                              />
                            ) : <SpinnerBotReplying />}
                          </MessageContainerBot>
                        )}
                      </MessageContainer>
                    )
                  })}
                  {isBotThinking && (
                    <MessageContainerBot>
                      <MessageBotImage src={chatbotLogo} alt='Chatbot Logo' />
                      <MessageBotLoading>SH+ Assistant is typing</MessageBotLoading>
                    </MessageContainerBot>
                  )}
                  {isError && (
                    <MessageContainerBot>
                      <MessageBotImage src={chatbotLogo} alt='Chatbot Logo' />
                      <MessageError>
                        <AlertIcon fill={pallette.main} /> There was an error
                        connecting to bot. Click{' '}
                        <RetryButton onClick={() => retrySendMessage()}>
                          HERE
                        </RetryButton>{' '}
                        to retry or please come back later.
                      </MessageError>
                    </MessageContainerBot>
                  )}
                  {sessionExpiredError && (
                    <MessageContainerBot>
                      <MessageBotImage src={chatbotLogo} alt='Chatbot Logo' />
                      <MessageError>
                        <AlertIcon fill={pallette.main} /> Your session has
                        expired. Click{' '}
                        <RetryButton onClick={() => handleExpiredSession()}>
                          HERE
                        </RetryButton>{' '}
                        to log in again.
                      </MessageError>
                    </MessageContainerBot>
                  )}
                </AllMessagesContent>
              )}
            </AllMessagesContainer>
          ) : null}

          <InputWrapper>
            {messages.length === 0 ? (
              <ChatbotTitle>Hi, I'm your SH+ Assistant</ChatbotTitle>
            ) : (
              <ClearChatButton disabled={isBotReplying} onClick={() => handleClearChat()}>
                <ClearChatButtonText>Clear Chat</ClearChatButtonText>
              </ClearChatButton>
            )}
            <InputContainer isStartingConversation={messages.length === 0}>
              <Input
                ref={inputRef}
                value={inputText}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={isBotReplying || isError}
                placeholder='How can I help you?'
                isStartingConversation={messages.length === 0}
                maxLength={maxInputLength}
              />
              <SendContainer>
                <InputTextLengthIndicator>{inputText.length}/{maxInputLength}</InputTextLengthIndicator>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={isBotReplying || isError}
                >
                  <SendAltIcon color={pallette.main} />
                </SendButton>
              </SendContainer>
            </InputContainer>
            <CaveatTextContainer>
              <CaveatText>SH+ Assistant is AI powered and can make mistakes. See <CaveatTextLink href="/">instructions</CaveatTextLink> for more guidance.</CaveatText>
            </CaveatTextContainer>
          </InputWrapper>
        </ChatContainer>
      </Container>
    </Layout>
  );
};

export default Chatbot;
